import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type InputSignal,
  type OutputEmitterRef,
  type Signal,
} from '@angular/core';
import { UI_SEARCH_BAR_ACTIONS } from '../../constants/ui-search-bar-actions.constants';
import { UiSearchBarAction } from '../../types/ui-search-bar-action.interface';

@Component({
  selector: 'ui-search-bar',
  imports: [],
  templateUrl: './ui-search-bar.component.html',
  styleUrl: './ui-search-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiSearchBarComponent {
  private static nextId = 0;

  public readonly id: InputSignal<string | null> = input<string | null>(null);
  public readonly label: InputSignal<string | null> = input<string | null>(null);
  public readonly value: InputSignal<string> = input<string>('');
  public readonly placeholder: InputSignal<string> = input<string>('Szukaj');
  public readonly ariaLabel: InputSignal<string | null> = input<string | null>(null);
  public readonly disabled: InputSignal<boolean> = input<boolean>(false);
  public readonly actions: InputSignal<readonly UiSearchBarAction[]> =
    input<readonly UiSearchBarAction[]>(UI_SEARCH_BAR_ACTIONS);
  public readonly valueChange: OutputEmitterRef<string> = output<string>();
  public readonly submitted: OutputEmitterRef<string> = output<string>();
  public readonly blurred: OutputEmitterRef<void> = output<void>();
  public readonly actionSelected: OutputEmitterRef<string> = output<string>();

  protected readonly fallbackId: string = `ui-search-bar-${(UiSearchBarComponent.nextId += 1)}`;
  protected readonly inputId: Signal<string> = computed((): string => this.id() ?? this.fallbackId);
  protected readonly hasLabel: Signal<boolean> = computed((): boolean => !!this.label());
  protected readonly hasActions: Signal<boolean> = computed((): boolean => this.actions().length > 0);
  protected readonly inputAriaLabel: Signal<string | null> = computed((): string | null => {
    if (this.hasLabel()) {
      return null;
    }

    return this.ariaLabel() ?? this.placeholder();
  });

  protected handleInput(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;

    this.valueChange.emit(target.value);
  }

  protected handleBlur(): void {
    this.blurred.emit();
  }

  protected handleSubmit(event: Event): void {
    event.preventDefault();

    if (this.disabled()) {
      return;
    }

    this.submitted.emit(this.value());
  }

  protected handleActionSelected(action: UiSearchBarAction, event: MouseEvent): void {
    if (this.disabled() || action.disabled === true) {
      event.preventDefault();
      return;
    }

    this.actionSelected.emit(action.id);
  }
}
